import { EMPTY_VALUE_PLACEHOLDER } from '../constants';

/**
 * Map of short language codes to full BCP 47 locale strings.
 * Used by Intl.DateTimeFormat for locale-aware formatting.
 */
const LOCALE_MAP: Record<string, string> = {
  ar: 'ar-SA',
  en: 'en-US',
  fr: 'fr-FR',
};

/**
 * Resolve a short language code (e.g. 'en', 'fr', 'ar') to a full locale string.
 * Falls back to 'en-US' for unknown language codes.
 */
export function resolveLocale(language: string): string {
  return LOCALE_MAP[language] ?? 'en-US';
}

/**
 * Format the current date as a long display string for page banners.
 * Example: "Wednesday, March 18, 2026"
 */
export function formatLongDate(language: string): string {
  return new Date().toLocaleDateString(resolveLocale(language), {
    day: 'numeric',
    month: 'long',
    weekday: 'long',
    year: 'numeric',
  });
}

/**
 * Format a nullable date with medium date and short time for display.
 * Returns '—' (em dash) for null/undefined input.
 * Example: "Mar 18, 2026, 3:30 PM"
 */
export function formatDateTimeMedium(date: Date | null | undefined, language: string): string {
  if (!date) return EMPTY_VALUE_PLACEHOLDER;
  return new Intl.DateTimeFormat(resolveLocale(language), {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(new Date(date));
}

/**
 * Same medium-date + short-time formatting as `formatDateTimeMedium`, but
 * joined with a middle-dot separator instead of the locale's default
 * separator (a comma for 'en', a different connector word for other
 * locales such as French). The date and time parts are formatted
 * independently with the same `dateStyle`/`timeStyle` options as the sibling
 * formatter and then joined, rather than string-manipulating the combined
 * output — that keeps the result correct across locales instead of relying
 * on a comma that not every locale actually uses.
 * Returns em dash for null/undefined input.
 * Example (en): "May 24, 2026" + middle dot + "2:18 PM"
 */
export function formatDateTimeMediumDotted(date: Date | null | undefined, language: string): string {
  if (!date) return EMPTY_VALUE_PLACEHOLDER;
  const parsed = new Date(date);
  const locale = resolveLocale(language);
  const datePart = new Intl.DateTimeFormat(locale, { dateStyle: 'medium' }).format(parsed);
  const timePart = new Intl.DateTimeFormat(locale, { timeStyle: 'short' }).format(parsed);
  return `${datePart} \u00b7 ${timePart}`;
}
